import React from "react";
import { graphql } from "gatsby";
import { useI18n } from "@v4/utils/i18nContext";
import { entryMap } from "../../utils/entryMap";
import Link from "@v4/gatsby-theme-talend/src/components/Link";
import Clamp from "react-multiline-clamp";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import Image from "@v4/gatsby-theme-talend/src/components/Image/Image";

import * as newsEntryStyles from "./singleNewsEntryItem.module.css";
import classNames from "classnames/bind";
import { getLangPrefixedPath } from "@v4/utils/pathPrefix";
import { newsEntryTranslations } from "../../translations/singleNewsEntryItem";

export const PressReleaseFragment = graphql`
    fragment PressRelease on ContentfulPressRelease {
        id
        slug
        node_locale
        languages
        title
        date
        body {
            raw
            references {
                __typename
                ...RTEReferences
                ... on ContentfulCustomer {
                    contentful_id
                    slug
                }
            }
        }
        form {
            __typename
            ... on Node {
                ...marketoForm
            }
        }
    }
`;

export const ArticleFragment = graphql`
    fragment Article on ContentfulNewsArticle {
        id
        url
        node_locale
        title
        date
        logo {
            # fluid (maxWidth: 240, transformations: "c_pad,w_240,h_240")
            public_id
            width
            height
            format
        }
        logoAltText
        description {
            raw
        }
    }
`;

// Used for both Press Release and News Article listing pages
const SingleNewsEntryItem = ({
    title,
    date,
    slug,
    url,
    body,
    description,
    logo,
    logoAltText,
    node_locale,
    presentation = "List",
}) => {
    // CX binding method for styles
    const cx = classNames.bind(newsEntryStyles);

    // Set up i18n context and helper
    const { i18n } = useI18n();
    const translations = newsEntryTranslations[i18n.curr.langCode];

    // Format the date for the current language
    const dateFormatted = i18n.getLocalisedDate(date, null, i18n.curr.langName);

    // For when a logo is present
    const logoImage = logo ? logo[0] : false;
    const logoImageStyle = logo
        ? {
              width: "120px",
              height: "120px",
          }
        : null;
    const logoImageAltText = logoAltText ?? "";

    // Define what sort of visual layout the content will take
    const presentationClass = `newsEntriesPresentation${presentation}`;

    // Get proper URL
    let pageUrl = slug ? getLangPrefixedPath(`${entryMap.pressReleases.pagePath}/${slug}`, node_locale) : url;

    // Create a plain-text version of the PR's body for use with the Clamp package
    let plainDescription = body?.raw ?? description?.raw;
    plainDescription = plainDescription ? documentToPlainTextString(JSON.parse(plainDescription)) : "";

    return (
        // newWindow attribute relies on the presence of an external url prop; does not use contentful new-window field
        <Link to={pageUrl} className={cx("newsEntryItem", presentationClass)} newWindow={Boolean(url)}>
            {logo && (
                <div className={cx("newsEntryLogoWrapper")}>
                    <Image image={logoImage} style={logoImageStyle} alt={logoImageAltText} />
                </div>
            )}

            <div className={cx("newsEntryCopyWrapper")}>
                {presentation === "Card" && (
                    <h5 className={`${cx("newsEntryCardLabel")} bgGradientEventGradientDark`}>
                        {translations.pressRelease}
                    </h5>
                )}
                <time dateTime={date} className={cx("newsEntryDate")}>
                    {dateFormatted}
                </time>

                <h3 className={cx("newsEntryTitle")}>{title}</h3>

                {presentation !== "Card" && (
                    <div className={cx("newsEntryDescription")}>
                        <Clamp lines={3} withTooltip={false}>
                            {plainDescription}
                        </Clamp>
                    </div>
                )}

                <span className={cx("newsEntryReadMore")}>{translations.readMore}</span>
            </div>
        </Link>
    );
};

export default SingleNewsEntryItem;
